import { asAnyObject } from "@cervval.js/common/src/tools/any.tools";

export const DISPOSABLE_CSS_ATTRIBUTE = "cjs-disposable";


export interface Disposable {
    dispose: (p?: { doNotDisposeChildren?: boolean }) => void;
}

/** Tags the element as disposable (meaning it has a dispose function) */
export function setDisposable(element: HTMLElement & Disposable): void {
    element.setAttribute(DISPOSABLE_CSS_ATTRIBUTE, "true");
}

/**
 * calls hammer.destroy() and removes hammer from elementsToDispose if hammer is defined
 */
export function disposeHammer(elementsToDispose: { hammer?: HammerManager }): void {
    if (elementsToDispose.hammer) {
        elementsToDispose.hammer.destroy();
        delete elementsToDispose.hammer;
    }
}

/**
 * calls hammer.destroy() and removes hammer from elementsToDispose if hammers are defined
 */
export function disposeHammers(elementsToDispose: { hammers?: HammerManager[] }): void {
    if (elementsToDispose.hammers) {
        for (const hammer of elementsToDispose.hammers) {
            hammer.destroy();
        }
        delete elementsToDispose.hammers;
    }
}

/** add hammer to array */
export function registerHammerForDisposal(elementsToDispose: { hammers?: HammerManager[] }, ...hammers: HammerManager[]): void {
    elementsToDispose.hammers ??= [];
    elementsToDispose.hammers.push(...hammers);
}

/** add resizeObserver to array */
export function registerResizeObserverForDisposal(elementsToDispose: { resizeObservers?: ResizeObserver[] }, ...resizeObservers: ResizeObserver[]): void {
    elementsToDispose.resizeObservers ??= [];
    elementsToDispose.resizeObservers.push(...resizeObservers);
}

/**
 * calls resizeObserver.disconnect() and removes resizeObserver from elementsToDispose if resizeObserver is defined
 */
export function disposeResizeObserver(elementsToDispose: { resizeObserver?: ResizeObserver }): void {
    if (elementsToDispose.resizeObserver) {
        elementsToDispose.resizeObserver.disconnect();
        delete elementsToDispose.resizeObserver;
    }
}

/**
 * calls resizeObserver.disconnect() and removes resizeObserver from elementsToDispose if resizeObservers are defined
 */
export function disposeResizeObservers(elementsToDispose: { resizeObservers?: ResizeObserver[] }): void {
    if (elementsToDispose.resizeObservers) {
        for (const resizeObserver of elementsToDispose.resizeObservers) {
            resizeObserver.disconnect();
        }
        delete elementsToDispose.resizeObservers;
    }
}

/**
 * calls tooltip.dispose() and removes tooltip from elementsToDispose if tooltip is defined
 */
export function disposeTooltip(elementsToDispose: { tooltip?: { dispose: () => void } }): void {
    if (elementsToDispose.tooltip) {
        elementsToDispose.tooltip.dispose?.();

        delete asAnyObject(elementsToDispose.tooltip)["_element"];
        delete elementsToDispose.tooltip;
    }
}

/**
 * calls dispose on all children that have DISPOSABLE_CSS_ATTRIBUTE
 */
export function disposeChildren(element: HTMLElement): void {
    element.querySelectorAll("[" + DISPOSABLE_CSS_ATTRIBUTE + "=true]").forEach((element) => {
        const d = element as unknown as Disposable;
        if (typeof d.dispose === "function") {
            // we are disposing all children elements => they don't need to dispose their children
            d.dispose({ doNotDisposeChildren: true });
        }
    });
}
